import { inject, Injectable, TemplateRef, Type } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of, Subject, switchMap, take } from 'rxjs';
import { ModalType } from '../enums/modal-type.enum';
import { SmartModalAction } from '../enums/smart-modal-action.enum';
import { CustomModalOptions } from '../model/modal-options.model';
import { SmartModalComponent } from '../smart-modal.component';

@Injectable({
  providedIn: 'root',
})
export class SmartModalService {
  private modalService = inject(MatDialog);

  /**
   * Open smart modal.
   * Modal configuration and type are required params.
   * Returns an observable that hold the action performed on the modal.
   */
  openSmartModal(
    modalType: ModalType,
    modalOptions: CustomModalOptions
  ): Observable<SmartModalAction> {
    const modalRef = this.modalService.open(SmartModalComponent, {
      ...modalOptions.config,
      role: 'alertdialog',
    });

    modalRef.componentInstance.type.set(modalType);
    modalRef.componentInstance.modalOptions.set(modalOptions);
    return modalRef.afterClosed().pipe(switchMap(result => of(result)));
  }

  /**
   * Open component as a modal.
   */
  openModalFromComponent<T = unknown, R = unknown>(
    component: Type<T> | TemplateRef<T>,
    modalOptions?: CustomModalOptions
  ): Observable<R> {
    const modalRef = this.modalService.open(component, {
      data: modalOptions?.data,
      ...modalOptions?.config,
    });

    return modalRef.afterClosed().pipe(switchMap(result => of(result)));
  }

  /**
   * Open modal with guard function.
   * Returns an observable that hold the data emitted by the guard function.
   */
  openModalWithGuard<T, D>(
    component: Type<T>,
    modalOptions: CustomModalOptions,
    onBeforeClose: (
      modalAction: SmartModalAction,
      data?: D
    ) => Observable<boolean>
  ): Observable<D> {
    const dialogRef = this.modalService.open(component, {
      ...modalOptions?.config,
      data: modalOptions?.data,
      disableClose: true,
    });

    const closeGuard$ = new Subject<D>();

    dialogRef.componentInstance['allowClose'] = (
      modalAction: SmartModalAction,
      data: D
    ) => {
      onBeforeClose(modalAction, data).subscribe(canClose => {
        if (canClose) {
          closeGuard$.next(data);
          dialogRef.close();
        }
      });
    };

    return closeGuard$.asObservable().pipe(take(1)) as Observable<D>;
  }

  /**
   * Close all active smart modal.
   */
  closeActiveSmartModal(): void {
    this.modalService.closeAll();
  }
}
